import { useSelector } from "react-redux";
import { selectedActuarialStatus, selectedCurrentProposalStatusName, selectedEbamStatus, selectedProposalPreparedById } from "../../features/gms-state-and-modal/state-slice";
import { selectUserDepartmentCode, selectUserId } from "../../features/auth/auth-slice";
import { selectAmountOfLoans } from "../../features/gms-proposal-forms/amount-of-loans/amount-loans.slice";
import { selectSelectedGroupClassification, selectSelectedTypeOfGroup } from "../../features/gms-proposal-forms/gc-tog-ea-mop/gc-tog-ea-mop-slice";
import { selectBasicPlansState, selectCoverageTypeState, selectProductTypeState, selectProposalTypeState } from "../../features/gms-proposal-forms/types-proposal-product-basic-plan/types-proposal-product-basic-plan-slice";
import { selectApprovalData } from "../../features/actuarial/approval/approval-slice";
import { useProposalTypeData } from "./use-proposal-type-data";

export const useIsCompleteProposalAs = (): boolean => {

    const { 
        showInputChannelType, 
        channelType, 
        proposalAs, 
        channelTypeSubName, 
        channelTypeNumber, 
        channelTypeEmail,
    } = useProposalTypeData();

    const isBasicComplete =
        proposalAs !== null &&
        proposalAs.name !== "Select" &&
        channelType !== null &&
        channelType.name !== "Select";

    if (!isBasicComplete) {
        return false;
    }

    if (showInputChannelType) {
        return (
            channelTypeSubName?.trim() !== "" &&
            channelTypeNumber !== null &&
            channelTypeEmail?.trim() !== ""
        );
    }

    return true;
};

export const useIsSameUser = (): boolean => {
    const preparerId = useSelector(selectedProposalPreparedById);
    const userId = useSelector(selectUserId);

    return preparerId === userId;
};

export const useCurrentProposalStatus = (status: string): boolean => {
    const proposalStatus = useSelector(selectedCurrentProposalStatusName);
    const currentStatus = proposalStatus?.trim()?.toLowerCase();
    const finalStatus = status.trim().toLowerCase();
    
    return currentStatus === finalStatus;
}

export const useCurrentActuarialStatus = (status: string): boolean => {
    const actuarialStatus = useSelector(selectedActuarialStatus);
    const currentStatus = actuarialStatus[0]?.status_name?.trim().toLowerCase() ?? "";
    const finalStatus = status.trim().toLowerCase();
    
    return currentStatus === finalStatus;
}

export const useCurrentPolicyStatus = (status: string): boolean => {
    const policyStatus = useSelector(selectedEbamStatus);
    const currentStatus = policyStatus[0]?.status_name?.trim().toLowerCase() ?? "";
    const finalStatus = status.trim().toLowerCase();
    
    return currentStatus === finalStatus;
}

export const useCurrentUserDepartmentCode = (departmentCode: string): boolean => {
    const userDepartmentCode = useSelector(selectUserDepartmentCode);
    const currentDepartmentCode = userDepartmentCode?.trim()?.toLowerCase();
    const finalDepartmentCode = departmentCode.trim().toLowerCase();

    return currentDepartmentCode === finalDepartmentCode;
}

export const useCurrentProposalType = (id: number): boolean => {
    const proposalType = useSelector(selectProposalTypeState);

    return proposalType?.id === id
}

export const useCurrentProduct = (id: number): boolean => {
    const product = useSelector(selectProductTypeState);

    return product?.id === id;
}

export const useCurrentCoverageType = (id: number): boolean => {
    const coverage = useSelector(selectCoverageTypeState);
    
    return coverage?.id === id;
}

export const useCurrentOtherInput = (id: number, type: 'tog' | 'gc' | 'bt'): boolean => {
    const typeOfGroup = useSelector(selectSelectedTypeOfGroup);
    const groupClassification = useSelector(selectSelectedGroupClassification);

    switch (type) {
        case 'tog':
        return typeOfGroup?.id === id;

        case 'gc':
        return groupClassification?.id === id;

        default:
        return false;
    }
}

export const useCurrentSubType = (id: number): boolean => {
    const typeOfGroup = useSelector(selectSelectedTypeOfGroup);

    return typeOfGroup?.id === id;
}

export const useCurrentAmountOfLoans = (id: number): boolean => {
    const amountOfLoansType = useSelector(selectAmountOfLoans);

    return amountOfLoansType?.id === id;
}

export const useCurrentBasicPlanType = (id: number): boolean => {
    const basicPlanType = useSelector(selectBasicPlansState);

    return basicPlanType?.id === id;
}

export const useIsCurrentUserRateEditor = (): boolean => {
    const currentUserId = useSelector(selectUserId);
    const approvalData = useSelector(selectApprovalData);
    const endorsedByUserId = approvalData?.endorsed_by_user_id;

    // No editor yet — any authorized user can edit
    if (endorsedByUserId === null || endorsedByUserId === undefined) {
        return true;
    }

    // An editor already exists — only that user can edit
    return (
        currentUserId !== null &&
        Number(endorsedByUserId) === currentUserId
    );
}